import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency, formatDate } from '../../lib/utils';
import { 
  Clock, CheckCircle, ChefHat, Package, Truck, XCircle, Ban,
  MapPin, Phone, Store, ArrowLeft, RefreshCw, AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { 
    label: 'Aguardando Confirmação', 
    description: 'Seu pedido foi recebido e está aguardando confirmação do estabelecimento.',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    icon: Clock,
    step: 1
  },
  confirmed: { 
    label: 'Confirmado', 
    description: 'Ótimo! Seu pedido foi confirmado e logo começará a ser preparado.',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    icon: CheckCircle,
    step: 2
  },
  preparing: { 
    label: 'Em Preparação', 
    description: 'Seu pedido está sendo preparado com carinho.',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    icon: ChefHat,
    step: 3
  },
  ready: { 
    label: 'Pronto', 
    description: 'Seu pedido está pronto! Em breve sairá para entrega.',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    icon: Package,
    step: 4
  },
  delivered: { 
    label: 'Entregue', 
    description: 'Pedido entregue! Bom apetite!',
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    icon: Truck,
    step: 5
  },
  cancelled: { 
    label: 'Cancelado', 
    description: 'Este pedido foi cancelado.',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    icon: XCircle,
    step: -1
  },
  rejected: { 
    label: 'Rejeitado', 
    description: 'Infelizmente este pedido foi rejeitado pelo estabelecimento.',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    icon: Ban,
    step: -1
  }
};

const STEPS = [
  { status: 'pending', label: 'Recebido' },
  { status: 'confirmed', label: 'Confirmado' },
  { status: 'preparing', label: 'Preparando' },
  { status: 'ready', label: 'Pronto' },
  { status: 'delivered', label: 'Entregue' }
];

export default function OrderTracking() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [establishment, setEstablishment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchOrder = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/track`);
      setOrder(response.data.order);
      setEstablishment(response.data.establishment);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.detail || 'Pedido não encontrado');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
    // Polling every 15 seconds
    const interval = setInterval(fetchOrder, 15000);
    return () => clearInterval(interval);
  }, [fetchOrder]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando pedido...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Pedido não encontrado</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => window.history.back()} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[order?.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  const currentStep = statusConfig.step;
  const isNegativeStatus = currentStep === -1;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {establishment?.logo_url ? (
                <img src={establishment.logo_url} alt={establishment.name} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Store className="w-5 h-5 text-red-500" />
                </div>
              )}
              <div>
                <h1 className="font-semibold text-slate-800">{establishment?.name}</h1>
                <p className="text-xs text-slate-500">Acompanhe seu pedido</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchOrder}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Order Number Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-slate-500">Número do Pedido</p>
              <p className="text-2xl font-bold text-slate-800 font-mono">{order?.order_number}</p>
            </div>
            <div className={`p-3 rounded-full ${statusConfig.bgColor}`}>
              <StatusIcon className={`w-8 h-8 ${statusConfig.color}`} />
            </div>
          </div>
          
          {/* Current Status */}
          <div className={`p-4 rounded-lg ${statusConfig.bgColor}`}>
            <h2 className={`text-lg font-semibold ${statusConfig.color}`}>{statusConfig.label}</h2>
            <p className="text-slate-600 text-sm mt-1">{statusConfig.description}</p>
          </div>

          {/* Rejection Reason */}
          {order?.status === 'rejected' && order?.rejection_reason && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>Motivo:</strong> {order.rejection_reason}
              </p>
            </div>
          )}

          {/* Last Update */}
          <p className="text-xs text-slate-400 mt-4 text-center">
            Última atualização: {lastUpdate.toLocaleTimeString('pt-BR')}
          </p>
        </div>

        {/* Timeline Progress */}
        {!isNegativeStatus && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h3 className="font-semibold text-slate-800 mb-4">Progresso do Pedido</h3>
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-slate-200"></div>
              <div 
                className="absolute left-4 top-4 w-0.5 bg-green-500 transition-all duration-500"
                style={{ height: `${Math.max(0, (currentStep - 1) / (STEPS.length - 1) * 100)}%` }}
              ></div>
              
              {/* Steps */}
              <div className="space-y-6">
                {STEPS.map((step, index) => {
                  const stepNumber = index + 1;
                  const isCompleted = currentStep > stepNumber;
                  const isCurrent = currentStep === stepNumber;
                  const StepIcon = STATUS_CONFIG[step.status].icon;
                  
                  return (
                    <div key={step.status} className="flex items-center gap-4">
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center z-10
                        ${isCompleted ? 'bg-green-500 text-white' : 
                          isCurrent ? `${STATUS_CONFIG[step.status].bgColor} ${STATUS_CONFIG[step.status].color}` :
                          'bg-slate-200 text-slate-400'}
                      `}>
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <StepIcon className="w-4 h-4" />
                        )}
                      </div>
                      <div className={`flex-1 ${isCurrent ? 'font-medium' : ''} ${isCompleted || isCurrent ? 'text-slate-800' : 'text-slate-400'}`}>
                        {step.label}
                      </div>
                      {isCurrent && (
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 animate-pulse">
                          Agora
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Delivery Fee Notice */}
        {order?.delivery_type === 'delivery' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Aviso sobre Taxa de Entrega</p>
                <p className="text-xs text-amber-700 mt-1">
                  A taxa de entrega pode ser ajustada pelo estabelecimento conforme a distância ou localização.
                  {order?.delivery_fee > 0 && (
                    <span className="block mt-1 font-medium">
                      Taxa atual: {formatCurrency(order.delivery_fee)}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Order Details */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h3 className="font-semibold text-slate-800 mb-4">Detalhes do Pedido</h3>
          
          {/* Items */}
          <div className="space-y-3 mb-4">
            {order?.items.map((item, index) => (
              <div key={index} className="flex justify-between items-start py-2 border-b border-slate-100 last:border-0">
                <div className="flex-1">
                  <p className="font-medium text-slate-800">
                    {item.quantity}x {item.product_name}
                  </p>
                  {/* Show variation if selected */}
                  {item.variation && (
                    <p className="text-sm text-indigo-600 mt-0.5">
                      → {item.variation.name} ({formatCurrency(item.variation.price)})
                    </p>
                  )}
                  {item.addons?.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {item.addons.map((addon, ai) => {
                        const isFree = addon.free_quantity > 0 && addon.free_quantity >= addon.quantity;
                        const isPremium = addon.is_premium;
                        return (
                          <p key={ai} className="text-xs text-slate-500">
                            + {addon.quantity}x {addon.name}
                            {isPremium && (
                              <span className="ml-1 px-1 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded">PREMIUM</span>
                            )}
                            {isFree && !isPremium ? (
                              <span className="ml-1 text-green-600">(grátis)</span>
                            ) : addon.price > 0 ? (
                              <span className="ml-1">({formatCurrency(addon.price * addon.quantity)})</span>
                            ) : null}
                          </p>
                        );
                      })}
                    </div>
                  )}
                  {item.notes && (
                    <p className="text-xs text-slate-400 italic mt-1">Obs: {item.notes}</p>
                  )}
                </div>
                <p className="font-medium text-slate-800">{formatCurrency(item.total || (item.unit_price * item.quantity))}</p>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t border-slate-200 pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span className="text-slate-800">{formatCurrency(order?.subtotal || 0)}</span>
            </div>
            {order?.delivery_type === 'delivery' && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Taxa de Entrega</span>
                <span className="text-slate-800">{formatCurrency(order?.delivery_fee || 0)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>Total</span>
              <span className="text-green-600">{formatCurrency(order?.total || 0)}</span>
            </div>
          </div>
        </div>

        {/* Delivery Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h3 className="font-semibold text-slate-800 mb-4">Informações de Entrega</h3>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                <Package className="w-4 h-4 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Tipo</p>
                <p className="text-slate-800">{order?.delivery_type === 'delivery' ? 'Entrega' : 'Retirada'}</p>
              </div>
            </div>
            
            {order?.customer_address && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Endereço</p>
                  <p className="text-slate-800">{order.customer_address}</p>
                </div>
              </div>
            )}
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                <Phone className="w-4 h-4 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Telefone</p>
                <p className="text-slate-800">{order?.customer_phone}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Store */}
        {establishment?.whatsapp && (
          <div className="text-center mb-6">
            <a
              href={`https://wa.me/${establishment.whatsapp.replace(/\D/g, '')}?text=Olá! Gostaria de informações sobre meu pedido ${order?.order_number}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <Phone className="w-5 h-5" />
              Falar com o Estabelecimento
            </a>
          </div>
        )}

        {/* Back to Store */}
        {establishment?.slug && (
          <div className="text-center">
            <Link
              to={`/loja/${establishment.slug}`}
              className="text-sm text-slate-500 hover:text-slate-700 underline"
            >
              Fazer outro pedido
            </Link>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-xs text-slate-400">
        <p>Pedido realizado em {formatDate(order?.created_at)}</p>
      </footer>
    </div>
  );
}
